import React, { useEffect, useState } from 'react';
import {
  Button,
  Modal,
  Fade,
  Box,
  MenuItem,
  Typography,
  Backdrop,
  InputLabel,
  Select,
  TextField,
  Link,
  Collapse,
  Tooltip,
  IconButton,
} from '@material-ui/core';
import { InfoOutlined as InfoOutlinedIcon } from '@material-ui/icons';
import useStyles from '../../../styles';
import { REQUIRED_HIERARCHY_FIELD, getRequiredAnnotationKeys } from './validation';

export const MetadataModal = (props) => {
  const { md, setFormMd, entryType, entrySubType } = props;
  const [isOpen, setIsOpen] = useState(false);
  const [metadata, setMetadata] = useState(md ? JSON.parse(md) : {});

  const requiredHierarchyField = REQUIRED_HIERARCHY_FIELD[entryType];
  const requiredHierarchy = Boolean(requiredHierarchyField);
  const requiredAnnotationKeys = getRequiredAnnotationKeys({ entryType, entrySubType });
  const requiredAnnotation = requiredAnnotationKeys.length > 0;

  const [headerMap, setHeaderMap] = useState({
    // statically set
    freeze_asset_details: false,
    ira: false,
    fx_custody_bank_accounts: false,

    // dynamically set
    hierarchy: requiredHierarchy,
    annotation: requiredAnnotation,
  });
  useEffect(() => {
    setHeaderMap((prev) => ({
      ...prev,
      hierarchy: requiredHierarchy || prev.hierarchy,
      annotation: requiredAnnotation || prev.annotation,
    }));
  }, [requiredHierarchy, requiredAnnotation]);

  const initialAnnotations = () => {
    const existing = (md && md.annotation) || {};
    return Object.entries(existing).filter(([k]) => !requiredAnnotationKeys.includes(k));
  };

  const [annotations, setAnnotations] = useState(initialAnnotations());

  useEffect(() => {
    if (requiredAnnotationKeys.length === 0) return;
    setMetadata((prev) => {
      const currentAnnotation = prev.annotation || {};
      let changed = false;
      const next = { ...currentAnnotation };
      requiredAnnotationKeys.forEach((k) => {
        if (!(k in next)) {
          next[k] = '';
          changed = true;
        }
      });
      if (!changed) return prev;
      return { ...prev, annotation: next };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entryType, entrySubType]);

  const classes = useStyles();

  useEffect(() => {
    if (JSON.stringify(annotations) === '{}') return;
    setMetadata((prev) => {
      const freeform = Object.fromEntries(annotations);
      const prevAnnotation = prev.annotation || {};
      const required = Object.fromEntries(
        requiredAnnotationKeys.map((k) => [k, prevAnnotation[k] || ''])
      );
      return {
        ...prev,
        annotation: { ...required, ...freeform },
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [annotations]);

  const setRequiredAnnotation = (key, value) => {
    setMetadata((prev) => ({
      ...prev,
      annotation: {
        ...(prev.annotation || {}),
        [key]: value,
      },
    }));
  };

  const titleCase = (s) =>
    s.replace(/^_*(.)|_+(.)/g, (m, c, d) =>
      c ? c.toUpperCase() : ' ' + d.toUpperCase()
    );

  return (
    <>
      <Link
        onClick={() => setIsOpen(true)}
        color="primary"
        style={{ display: 'flex', marginTop: '8px', cursor: 'pointer' }}
      >
        Edit
      </Link>
      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        className={classes.modalBackdrop}
        open={isOpen}
        onClose={() => setIsOpen(false)}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 500 }}
      >
        <Fade in={isOpen}>
          <div className={classes.fadeModalMd}>
            <Typography
              id="transition-modal-title"
              variant="h4"
              className={classes.textBold}
              gutterBottom
            >
              Edit Metadata
            </Typography>
            {requiredHierarchy && (
              <Box
                bgcolor="#fff4e5"
                borderRadius="5px"
                padding="10px"
                mb={2}
              >
                <strong>{entryType} requires:</strong> Hierarchy &rarr;{' '}
                {titleCase(requiredHierarchyField)} ({requiredHierarchyField}).
              </Box>
            )}
            {requiredAnnotationKeys.length > 0 && (
              <Box
                bgcolor="#fff4e5"
                borderRadius="5px"
                padding="10px"
                mb={2}
              >
                <strong>
                  {entryType}{entrySubType ? ` (${entrySubType})` : ''} requires
                  annotation keys:
                </strong>{' '}
                {requiredAnnotationKeys.join(', ')}.
              </Box>
            )}
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              gridGap={10}
              bgcolor="#00000010"
              borderRadius="5px"
              padding="14px"
            >
              <p style={{ fontWeight: 'bold', margin: 'auto' }}>Preview</p>
              {JSON.stringify(metadata)}
            </Box>
            <Box mt={5} maxHeight={'1000px'} overflow="scroll">
              <Box display="flex" flexDirection="column">
                <Box display="flex" flexDirection="row">
                  <div
                    className={classes.grdCell1}
                    style={{ marginRight: 30, marginTop: 16 }}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      style={{ position: 'relative', width: 'fit-content' }}
                    >
                      <InputLabel>To Open</InputLabel>
                      <Tooltip
                        classes={{ tooltip: classes.largeTooltip }}
                        title={
                          <>
                            <div>
                              For unnetted accounts, To Open and QTY select the
                              position bucket:
                            </div>
                            <ul
                              style={{ margin: '4px 0 0', paddingLeft: 18 }}
                            >
                              <li>
                                <strong>True + positive QTY:</strong> opens or
                                extends long
                              </li>
                              <li>
                                <strong>False + negative QTY:</strong> closes
                                long
                              </li>
                              <li>
                                <strong>True + negative QTY:</strong> opens or
                                extends short
                              </li>
                              <li>
                                <strong>False + positive QTY:</strong> closes
                                short
                              </li>
                            </ul>
                          </>
                        }
                      >
                        <IconButton
                          aria-label="Explain To Open position intent"
                          size="small"
                          style={{
                            position: 'absolute',
                            left: '100%',
                            marginLeft: 4
                          }}
                        >
                          <InfoOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Select
                      labelId="type"
                      fullWidth
                      onChange={(e) => {
                        setMetadata({ ...metadata, to_open: e.target.value });
                      }}
                    >
                      <MenuItem value="">
                        <em>Blank</em>
                      </MenuItem>
                      <MenuItem value={true}>True</MenuItem>
                      <MenuItem value={false}>False</MenuItem>
                    </Select>
                  </div>
                  <div
                    className={classes.grdCell1}
                    style={{ marginRight: 30, marginTop: 16 }}
                  >
                    <InputLabel>Allow Inactive Asset</InputLabel>
                    <Select
                      labelId="type"
                      fullWidth
                      onChange={(e) =>
                        setMetadata({
                          ...metadata,
                          allow_inactive_asset: e.target.value,
                        })
                      }
                    >
                      <MenuItem value="">
                        <em>Blank</em>
                      </MenuItem>
                      <MenuItem value={true}>True</MenuItem>
                      <MenuItem value={false}>False</MenuItem>
                    </Select>
                  </div>
                  <div
                    className={classes.grdCell1}
                    style={{ marginRight: 30, marginTop: 16 }}
                  >
                    <InputLabel>Local Fees</InputLabel>
                    <TextField
                      type="number"
                      fullWidth
                      onChange={(e) =>
                        setMetadata({ ...metadata, local_fees: e.target.value })
                      }
                    />
                  </div>
                </Box>
                <Box display="flex" flexDirection="row">
                  <div
                    className={classes.grdCell1}
                    style={{ marginRight: 30, marginTop: 16 }}
                  >
                    <InputLabel>Payable To</InputLabel>
                    <TextField
                      fullWidth
                      onChange={(e) =>
                        setMetadata({ ...metadata, payable_to: e.target.value })
                      }
                    />
                  </div>

                  <div
                    className={classes.grdCell1}
                    style={{ marginRight: 30, marginTop: 16 }}
                  >
                    <InputLabel>Receivable From</InputLabel>
                    <TextField
                      fullWidth
                      onChange={(e) =>
                        setMetadata({
                          ...metadata,
                          receivable_from: e.target.value,
                        })
                      }
                    />
                  </div>
                </Box>
                <SectionHeader
                  headerMap={headerMap}
                  headerKey="annotation"
                  setHeaderMap={setHeaderMap}
                />
                <Collapse in={headerMap['annotation']}>
                  {requiredAnnotationKeys.length > 0 && (
                    <Box
                      display="flex"
                      flexDirection="row"
                      flexWrap="wrap"
                      style={{ marginTop: 8, gap: 16 }}
                    >
                      {requiredAnnotationKeys.map((key) => (
                        <div
                          key={key}
                          className={classes.grdCell1}
                          style={{ minWidth: 200 }}
                        >
                          <InputLabel required>{titleCase(key)}</InputLabel>
                          <TextField
                            fullWidth
                            value={
                              (metadata.annotation && metadata.annotation[key]) ||
                              ''
                            }
                            onChange={(e) =>
                              setRequiredAnnotation(key, e.target.value)
                            }
                          />
                        </div>
                      ))}
                    </Box>
                  )}
                  <AnnotationEditor
                    annotations={annotations}
                    setAnnotations={setAnnotations}
                  />
                </Collapse>
                <SectionHeader
                  headerMap={headerMap}
                  headerKey="hierarchy"
                  setHeaderMap={setHeaderMap}
                />
                <Collapse in={headerMap['hierarchy']}>
                  <Box
                    display="flex"
                    flexDirection="row"
                    style={{ marginTop: 16 }}
                  >
                    <div
                      className={classes.grdCell1}
                      style={{ marginRight: 30 }}
                    >
                      <InputLabel required={requiredHierarchyField === 'parent'}>
                        Parent
                      </InputLabel>
                      <TextField
                        fullWidth
                        value={(metadata.hierarchy && metadata.hierarchy.parent) || ''}
                        onChange={(e) =>
                          setMetadata({
                            ...metadata,
                            hierarchy: {
                              ...metadata.hierarchy,
                              parent: e.target.value,
                            },
                          })
                        }
                      />
                    </div>
                    <div
                      className={classes.grdCell1}
                      style={{ marginRight: 30 }}
                    >
                      <InputLabel required={requiredHierarchyField === 'group'}>
                        Group
                      </InputLabel>
                      <TextField
                        fullWidth
                        value={(metadata.hierarchy && metadata.hierarchy.group) || ''}
                        onChange={(e) =>
                          setMetadata({
                            ...metadata,
                            hierarchy: {
                              ...metadata.hierarchy,
                              group: e.target.value,
                            },
                          })
                        }
                      />
                    </div>
                    <div
                      className={classes.grdCell1}
                      style={{ marginRight: 30 }}
                    >
                      <InputLabel>Children (comma separated)</InputLabel>
                      <TextField
                        fullWidth
                        onChange={(e) =>
                          setMetadata({
                            ...metadata,
                            hierarchy: {
                              ...metadata.hierarchy,
                              children: e.target.value.split(','),
                            },
                          })
                        }
                      />
                    </div>
                  </Box>
                </Collapse>
                <SectionHeader
                  headerMap={headerMap}
                  headerKey="freeze_asset_details"
                  setHeaderMap={setHeaderMap}
                />
                <Collapse in={headerMap['freeze_asset_details']}>
                  <Box
                    display="flex"
                    flexDirection="row"
                    style={{ marginTop: 16 }}
                  >
                    <div
                      className={classes.grdCell1}
                      style={{ marginRight: 30 }}
                    >
                      <InputLabel>Asset Type</InputLabel>
                      <TextField
                        fullWidth
                        onChange={(e) =>
                          setMetadata({
                            ...metadata,
                            freeze_asset_details: {
                              ...metadata.freeze_asset_details,
                              asset_type: e.target.value,
                            },
                          })
                        }
                      />
                    </div>
                    <div
                      className={classes.grdCell1}
                      style={{ marginRight: 30 }}
                    >
                      <InputLabel>Symbol</InputLabel>
                      <TextField
                        fullWidth
                        onChange={(e) =>
                          setMetadata({
                            ...metadata,
                            freeze_asset_details: {
                              ...metadata.freeze_asset_details,
                              symbol: e.target.value,
                            },
                          })
                        }
                      />
                    </div>
                    <div
                      className={classes.grdCell1}
                      style={{ marginRight: 30 }}
                    >
                      <InputLabel>Cusip</InputLabel>
                      <TextField
                        fullWidth
                        onChange={(e) =>
                          setMetadata({
                            ...metadata,
                            freeze_asset_details: {
                              ...metadata.freeze_asset_details,
                              cusip: e.target.value,
                            },
                          })
                        }
                      />
                    </div>
                    <div
                      className={classes.grdCell1}
                      style={{ marginRight: 30 }}
                    >
                      <InputLabel>Symbol Description</InputLabel>
                      <TextField
                        fullWidth
                        onChange={(e) =>
                          setMetadata({
                            ...metadata,
                            freeze_asset_details: {
                              ...metadata.freeze_asset_details,
                              symbol_description: e.target.value,
                            },
                          })
                        }
                      />
                    </div>
                  </Box>
                </Collapse>

                <SectionHeader
                  headerMap={headerMap}
                  headerKey="ira"
                  setHeaderMap={setHeaderMap}
                />
                <Collapse in={headerMap['ira']}>
                  <Box
                    display="flex"
                    flexDirection="row"
                    style={{ marginTop: 16 }}
                  >
                    <div
                      className={classes.grdCell1}
                      style={{ marginRight: 30 }}
                    >
                      <InputLabel>Tax Year</InputLabel>
                      <TextField
                        fullWidth
                        onChange={(e) =>
                          setMetadata({
                            ...metadata,
                            ira: {
                              ...metadata.ira,
                              tax_year: e.target.value,
                            },
                          })
                        }
                      />
                    </div>
                    <div
                      className={classes.grdCell1}
                      style={{ marginRight: 30 }}
                    >
                      <InputLabel>Fed. Withholding Percent</InputLabel>
                      <TextField
                        fullWidth
                        type="number"
                        onChange={(e) =>
                          setMetadata({
                            ...metadata,
                            ira: {
                              ...metadata.ira,
                              fed_withholding_pct: e.target.value,
                            },
                          })
                        }
                      />
                    </div>
                    <div
                      className={classes.grdCell1}
                      style={{ marginRight: 30 }}
                    >
                      <InputLabel>Fed. Withholding Amount</InputLabel>
                      <TextField
                        fullWidth
                        type="number"
                        onChange={(e) =>
                          setMetadata({
                            ...metadata,
                            ira: {
                              ...metadata.ira,
                              fed_withholding_amount: e.target.value,
                            },
                          })
                        }
                      />
                    </div>
                  </Box>
                  <Box
                    display="flex"
                    flexDirection="row"
                    style={{ marginTop: 16 }}
                  >
                    <div
                      className={classes.grdCell1}
                      style={{ marginRight: 30 }}
                    >
                      <InputLabel>State Withholding Percent</InputLabel>
                      <TextField
                        fullWidth
                        type="number"
                        onChange={(e) =>
                          setMetadata({
                            ...metadata,
                            ira: {
                              ...metadata.ira,
                              state_withholding_pct: e.target.value,
                            },
                          })
                        }
                      />
                    </div>
                    <div
                      className={classes.grdCell1}
                      style={{ marginRight: 30 }}
                    >
                      <InputLabel>State Withholding Amount</InputLabel>
                      <TextField
                        fullWidth
                        type="number"
                        onChange={(e) =>
                          setMetadata({
                            ...metadata,
                            ira: {
                              ...metadata.ira,
                              state_withholding_amount: e.target.value,
                            },
                          })
                        }
                      />
                    </div>
                  </Box>
                </Collapse>

                <SectionHeader
                  headerMap={headerMap}
                  headerKey="fx_custody_bank_accounts"
                  setHeaderMap={setHeaderMap}
                />
                <Collapse in={headerMap['fx_custody_bank_accounts']}>
                  <Box
                    display="flex"
                    flexDirection="row"
                    style={{ marginTop: 16 }}
                  >
                    <div
                      className={classes.grdCell1}
                      style={{ marginRight: 30 }}
                    >
                      <InputLabel>Account USD</InputLabel>
                      <TextField
                        fullWidth
                        onChange={(e) =>
                          setMetadata({
                            ...metadata,
                            fx_custody_bank_accounts: {
                              ...metadata.fx_custody_bank_accounts,
                              account_usd: e.target.value,
                            },
                          })
                        }
                      />
                    </div>
                    <div
                      className={classes.grdCell1}
                      style={{ marginRight: 30 }}
                    >
                      <InputLabel>Account Non USD</InputLabel>
                      <TextField
                        fullWidth
                        onChange={(e) =>
                          setMetadata({
                            ...metadata,
                            fx_custody_bank_accounts: {
                              ...metadata.fx_custody_bank_accounts,
                              account_non_usd: e.target.value,
                            },
                          })
                        }
                      />
                    </div>
                  </Box>
                </Collapse>
              </Box>
              <Box display="flex" flexDirection="row" mt="24px" gridGap="24px">
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={() => {
                    setIsOpen(false);
                    setFormMd(
                      { target: { value: JSON.stringify(metadata) } },
                      'metadata'
                    );
                  }}
                >
                  Save
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={() => setIsOpen(false)}
                >
                  Close
                </Button>
              </Box>
            </Box>
          </div>
        </Fade>
      </Modal>
    </>
  );
};

const AnnotationEditor = (props) => {
  const { annotations, setAnnotations } = props;

  return (
    <Box style={{ display: 'flex', flexDirection: 'column' }}>
      {Object.entries(annotations)
        .concat([['', '']])
        .map((a, idx) => (
          <Box
            style={{ display: 'flex', flexDirection: 'row', gap: '48px' }}
            key={idx}
          >
            <TextField
              fullWidth
              onChange={(e) => {
                let updatedArray = [];
                if (annotations.length > 0) {
                  updatedArray = [...annotations];
                }
                updatedArray[idx] = [e.target.value, a[1][1]];
                setAnnotations(updatedArray);
              }}
              value={a[1][0]}
            />
            <TextField
              fullWidth
              onChange={(e) => {
                let updatedArray = [];
                if (annotations.length > 0) {
                  updatedArray = [...annotations];
                }
                updatedArray[idx] = [a[1][0], e.target.value];
                setAnnotations(updatedArray);
              }}
              value={a[1][1]}
            />
          </Box>
        ))}
    </Box>
  );
};

const SectionHeader = (props) => {
  const { headerMap, headerKey, setHeaderMap } = props;
  const currVal = headerMap[headerKey];

  const titleCase = (s) =>
    s.replace(/^_*(.)|_+(.)/g, (s, c, d) =>
      c ? c.toUpperCase() : ' ' + d.toUpperCase()
    );

  return (
    <Box
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: '16px',
      }}
    >
      <p style={{ fontWeight: 'bold', marginTop: '16px' }}>
        {titleCase(headerKey)}
      </p>
      <Link
        onClick={() => setHeaderMap({ ...headerMap, [headerKey]: !currVal })}
        color="primary"
      >
        {currVal ? 'Hide' : 'Show'}
      </Link>
    </Box>
  );
};
